"use client";

import {
    TextInput,
    NumberInput,
    Checkbox,
    Button,
    Stack,
    Group,
    Text,
    rem,
    LoadingOverlay,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { UseFormReturnType } from "@mantine/form";
import type { Form } from "@bi-studio/shared/lib";
import { BaseContainer } from "@/components";

interface StandardFormDisplayProps {
    form: Form;
    mantineForm: UseFormReturnType<any>;
    sortedFields: any[];
    isFormIncomplete: boolean;
    submitting: boolean;
    onSubmit: (values: any) => Promise<void>;
    handleValidationError: () => void;
}

export function StandardFormDisplay({
    form,
    mantineForm,
    sortedFields,
    isFormIncomplete,
    submitting,
    onSubmit,
    handleValidationError,
}: StandardFormDisplayProps) {
    return (
        <BaseContainer style={{ position: 'relative' }}>
            <LoadingOverlay visible={submitting} />
            <div style={{ padding: rem(24) }}>
                <form onSubmit={mantineForm.onSubmit(onSubmit, handleValidationError)}>
                    <Stack gap="md">
                        {form.description && (
                            <Text c="dimmed" size="sm" mb="xs">
                                {form.description}
                            </Text>
                        )}
                        {sortedFields.map((field) => (
                            <div key={field.name}>
                                {field.type === 'number' && (
                                    <NumberInput
                                        label={field.label}
                                        placeholder={`Enter ${field.label.toLowerCase()}`}
                                        required={field.required}
                                        withAsterisk={field.required}
                                        {...mantineForm.getInputProps(field.name)}
                                    />
                                )}
                                {field.type === 'date' && (
                                    <DateInput
                                        label={field.label}
                                        placeholder="Pick date"
                                        required={field.required}
                                        withAsterisk={field.required}
                                        {...mantineForm.getInputProps(field.name)}
                                    />
                                )}
                                {field.type === 'checkbox' && (
                                    <Checkbox
                                        label={field.label}
                                        required={field.required}
                                        {...mantineForm.getInputProps(field.name, { type: 'checkbox' })}
                                    />
                                )}
                                {(field.type === 'text' || !['number', 'date', 'checkbox'].includes(field.type)) && (
                                    <TextInput
                                        label={field.label}
                                        placeholder={`Enter ${field.label.toLowerCase()}`}
                                        required={field.required}
                                        withAsterisk={field.required}
                                        {...mantineForm.getInputProps(field.name)}
                                    />
                                )}
                            </div>
                        ))}

                        <Group justify="flex-end" mt="xl">
                            <Button
                                type="submit"
                                loading={submitting}
                                disabled={isFormIncomplete}
                                size="md"
                                color="black"
                                radius="md"
                            >
                                Submit Form
                            </Button>
                        </Group>
                    </Stack>
                </form>
            </div>
        </BaseContainer>
    );
}
